import React, { useEffect, useMemo, useState } from "react";
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Switch,
  Tag,
  Typography,
} from "antd";
import { CodeOutlined, SendOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import { notify } from "../../../../services/toast.config";
import * as emailTemplateApi from "../../../../core/data/redux/services/emailTemplateApi";
import {
  isTypedVariable,
  normalizeVariables,
  type TypedVariable,
} from "../../../../core/common/email-templates/helpers/variables.helper";

import type { EmailTemplate } from "../../../../core/data/redux/email-templates/types";

const { Text } = Typography;

interface Props {
  open: boolean;
  template: EmailTemplate | null;
  companyId?: string;
  companyType?: string;
  onClose: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SYSTEM_VARS = new Set([
  "companyName",
  "productName",
  "logoUrl",
  "year",
  "supportEmail",
  "hrEmail",
  "footerNote",
]);

const extractPlaceholders = (template: EmailTemplate | null): string[] => {
  if (!template) return [];
  const sources = [template.subject || "", template.body_html || ""];
  const re = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  const seen = new Set<string>();
  for (const src of sources) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(src)) !== null) {
      const name = m[1];
      if (!SYSTEM_VARS.has(name)) seen.add(name);
    }
  }
  return Array.from(seen);
};

const placeholderForType = (v: TypedVariable): string => {
  switch (v.type) {
    case "email":
      return "name@example.com";
    case "url":
      return "https://example.com";
    case "number":
      return "0";
    case "date":
      return "YYYY-MM-DD";
    case "boolean":
      return "true / false";
    default:
      return v.sample !== undefined && v.sample !== null
        ? String(v.sample)
        : `[${v.key}]`;
  }
};

const buildInitialFromVariables = (
  variables: TypedVariable[],
): Record<string, any> => {
  const out: Record<string, any> = {};
  variables.forEach((v) => {
    if (v.sample !== undefined && v.sample !== null && v.sample !== "") {
      out[v.key] = v.sample;
    } else if (v.type === "boolean") {
      out[v.key] = false;
    } else {
      out[v.key] = "";
    }
  });
  return out;
};

const TestSendModal: React.FC<Props> = ({
  open,
  template,
  companyId,
  companyType,
  onClose,
}) => {
  const [toEmail, setToEmail] = useState("");
  const [values, setValues] = useState<Record<string, any>>({});
  const [sending, setSending] = useState(false);

  const typedVariables = useMemo<TypedVariable[]>(() => {
    const declared = normalizeVariables(template?.available_variables);
    const declaredKeys = new Set(declared.map((v) => v.key));
    const extracted = extractPlaceholders(template);
    const undeclared: TypedVariable[] = extracted
      .filter((key) => !declaredKeys.has(key))
      .map((key) => ({
        key,
        label: key,
        type: "string" as const,
        required: true,
        sample: undefined,
        description: "Used in the template but not declared in variables list.",
      }));
    return [...declared, ...undeclared];
  }, [template]);

  useEffect(() => {
    if (open && template) {
      setToEmail("");
      setValues(buildInitialFromVariables(typedVariables));
    }
  }, [open, template, typedVariables]);

  const handleFieldChange = (key: string, v: any) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  const handleSend = async () => {
    if (!template || !companyId || !companyType) return;
    if (!EMAIL_REGEX.test(toEmail.trim())) {
      notify.warn("Enter a valid recipient email");
      return;
    }

    const missing = typedVariables.filter((v) => {
      const val = values[v.key];
      if (v.type === "boolean") return val === undefined || val === null;
      return val === undefined || val === null || String(val).trim() === "";
    });
    if (missing.length > 0) {
      notify.warn(
        `Fill all variables before sending: ${missing.map((v) => v.label || v.key).join(", ")}`,
      );
      return;
    }

    try {
      setSending(true);
      await emailTemplateApi.testSendEmailTemplate(
        companyId,
        companyType,
        template.id,
        toEmail.trim(),
        values,
      );
      notify.success(`Test email sent to ${toEmail.trim()}`);
      onClose();
    } catch (e: any) {
      notify.error(
        e?.data?.message?.[0]?.msg ||
          (typeof e === "string" ? e : "Failed to send test email"),
      );
    } finally {
      setSending(false);
    }
  };

  const renderField = (v: TypedVariable) => {
    const value = values[v.key];

    if (v.type === "boolean") {
      return (
        <Switch
          checked={Boolean(value)}
          onChange={(checked) => handleFieldChange(v.key, checked)}
        />
      );
    }
    if (v.type === "number") {
      return (
        <InputNumber
          style={{ width: "100%" }}
          value={value === "" ? null : value}
          onChange={(val) => handleFieldChange(v.key, val)}
          placeholder={placeholderForType(v)}
        />
      );
    }
    if (v.type === "date") {
      const dayjsValue: Dayjs | null = value && dayjs(value).isValid() ? dayjs(value) : null;
      return (
        <DatePicker
          style={{ width: "100%" }}
          value={dayjsValue}
          onChange={(d) =>
            handleFieldChange(v.key, d ? d.format("YYYY-MM-DD") : "")
          }
        />
      );
    }
    if (v.type === "url") {
      return (
        <Input
          value={value || ""}
          onChange={(e) => handleFieldChange(v.key, e.target.value)}
          placeholder={placeholderForType(v)}
          prefix={<i className="ti ti-link" style={{ color: "#94a3b8" }} />}
        />
      );
    }
    if (v.type === "email") {
      return (
        <Input
          value={value || ""}
          onChange={(e) => handleFieldChange(v.key, e.target.value)}
          placeholder={placeholderForType(v)}
          prefix={<i className="ti ti-mail" style={{ color: "#94a3b8" }} />}
        />
      );
    }
    return (
      <Input
        value={value || ""}
        onChange={(e) => handleFieldChange(v.key, e.target.value)}
        placeholder={placeholderForType(v)}
      />
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSend}
      okText={
        <span>
          <SendOutlined /> Send Test
        </span>
      }
      confirmLoading={sending}
      title={
        <span>
          <SendOutlined /> Send Test Email
        </span>
      }
      destroyOnClose
      width={620}
      zIndex={1100}
    >
      {template ? (
        <>
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: 10,
              marginBottom: 14,
              fontSize: 12,
            }}
          >
            <div style={{ fontWeight: 600, color: "#0f172a" }}>
              {template.template_name}
            </div>
            <div style={{ color: "#475569", marginTop: 2 }}>
              <Tag style={{ fontFamily: "monospace", fontSize: 10 }}>
                {template.template_code}
              </Tag>
              {template.category ? (
                <Tag style={{ fontSize: 10 }}>{template.category}</Tag>
              ) : null}
            </div>
          </div>

          <Form layout="vertical" size="middle">
            <Form.Item label="Recipient email" required>
              <Input
                placeholder="you@example.com"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                onPressEnter={handleSend}
                autoFocus
                prefix={
                  <i className="ti ti-send" style={{ color: "#94a3b8" }} />
                }
              />
            </Form.Item>

            {typedVariables.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 12,
                  background: "#f8fafc",
                  borderRadius: 6,
                  fontSize: 12,
                  color: "#64748b",
                }}
              >
                <CodeOutlined /> This template has no variables declared. It
                will be sent as-is.
              </div>
            ) : (
              <>
                <div
                  style={{
                    fontSize: 11,
                    color: "#64748b",
                    marginBottom: 6,
                  }}
                >
                  Fill all variables before sending — empty placeholders would
                  render as blanks in the email.
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0 16px",
                  }}
                >
                  {typedVariables.map((v) => (
                    <Form.Item
                      key={v.key}
                      label={
                        <span>
                          {v.label || v.key} <Text type="danger">*</Text>
                          <Text
                            code
                            style={{ fontSize: 10, marginLeft: 6 }}
                          >
                            {`{{${v.key}}}`}
                          </Text>
                        </span>
                      }
                      help={v.description}
                      style={{ marginBottom: 8 }}
                    >
                      {renderField(v)}
                    </Form.Item>
                  ))}
                </div>
              </>
            )}
          </Form>
        </>
      ) : null}
    </Modal>
  );
};

export default TestSendModal;
